import { useModalStatusMessage } from '@/hooks';
import { useMutation } from '@apollo/client';
import UPDATE_DEVICE_USER_RELATIONS from '@features/access/graphql/mutations/updateDeviceUserRelations';
import { DevicesAccessForUserInput, DeviceUserValue } from '@features/devicesAccessInputs';
import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Typography } from '@mui/material';
import { useEffect, type FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { TabContent, TabProps } from '../common';
import { useGlobalModal } from '@/hooks/useGlobalModal';
import { Access } from '@features/authorization';

type FormValues = {
  userId: number;
  updatedRelations: DeviceUserValue[];
};

export const DeviceAccessTab: FC<TabProps> = ({ userId, onClose, setIsDirty }) => {
  const { _ } = useLingui();
  const { openModalErrorMessage, openModalSuccessMessage } = useModalStatusMessage();
  const { setModalState: setAddDevicesModalState } = useGlobalModal('addDeviceUserRelationsModal');
  const { setModalState: setRemoveDevicesModalState } = useGlobalModal(
    'removeDeviceUserRelationsModal',
  );

  const [updateRelations] = useMutation(UPDATE_DEVICE_USER_RELATIONS, {
    onCompleted() {
      methods.reset();
      openModalSuccessMessage(_(msg`Relaciones actualizadas correctamente`));
    },
    onError(error) {
      openModalErrorMessage(error.message);
    },
  });

  const methods = useForm<FormValues>({
    defaultValues: {
      userId,
      updatedRelations: [],
    },
  });

  useEffect(() => setIsDirty(methods.formState.isDirty), [methods.formState.isDirty, setIsDirty]);

  const onSubmit = methods.handleSubmit((data) => {
    updateRelations({
      variables: {
        inputs: data.updatedRelations,
      },
    });
  });

  const onAddMoreDevicesButtonClick = (userId: number) => () => {
    setAddDevicesModalState({
      open: true,
      fixedUserId: userId,
    });
  };

  const onRemoveDevicesButtonClick = (userId: number) => () => {
    setRemoveDevicesModalState({
      open: true,
      fixedUserId: userId,
    });
  };

  return (
    <form onSubmit={onSubmit}>
      <TabContent
        slotProps={{
          modalActions: {
            onClose,
            onSubmit,
            onResetForm: () => methods.reset(),
            submitLoading: methods.formState.isSubmitting,
            dirtyForm: methods.formState.isDirty,
            customButtons: [
              {
                onClick: onAddMoreDevicesButtonClick(userId),
                label: _(msg`Agregar más dispositivos`),
                authorization: {
                  access: Access.CreateDeviceUserAccess,
                },
              },
              {
                onClick: onRemoveDevicesButtonClick(userId),
                label: _(msg`Eliminar registros`),
                color: 'error',
                authorization: {
                  access: Access.DeleteDeviceUserAccess,
                },
              },
            ],
          },
        }}
      >
        <Typography variant="h6" mb={2}>
          <Trans>Dispositivos a los que tiene acceso el usuario</Trans>
        </Typography>
        <Controller
          control={methods.control}
          name="updatedRelations"
          render={({ field: { ref, ...props } }) => (
            <DevicesAccessForUserInput height={380} userId={userId} {...props} />
          )}
        />
      </TabContent>
    </form>
  );
};
